/**
 * Turning two disagreeing sentences into one inspectable comparison.
 *
 * A model may extract the fields below, but it never decides whether the claims
 * conflict under the recorded assumptions. Once the fields exist, classification
 * follows a closed vocabulary; that does not validate the extraction. The expensive part is
 * interpretation, while the loud label is something an auditor must be able to
 * reproduce without calling a model.
 */

import type { ScopeFieldOrigin } from './evidenceContinuity'

export type ScopeDimension =
  | 'population'
  | 'intervention'
  | 'exposure'
  | 'comparator'
  | 'outcome'
  | 'measurement'
  | 'timeHorizon'
  | 'studyDesign'
  | 'definition'
  | 'causalMode'
  | 'assumptions'

export type ClaimDirection = 'positive' | 'negative' | 'null' | 'mixed' | 'unspecified'
export type CausalMode = 'causal' | 'correlational' | 'descriptive' | 'unspecified'

export const CONTRADICTION_METHOD = 'scope-uncertainty-v2' as const
export const LEGACY_CONTRADICTION_METHOD = 'scope-direction-v1' as const
export const NULL_MEANINGS = ['non-significant', 'estimated-zero', 'equivalent', 'unknown'] as const
export type NullMeaning = (typeof NULL_MEANINGS)[number]

export interface QuantitativeFinding {
  estimate: number | null
  /** Both must name the same quantity before intervals can be compared. */
  estimand: string
  unit: string
  /** For example 0 for a difference, 1 for a ratio. Never inferred from direction. */
  nullValue: number | null
  interval: {
    kind: 'confidence' | 'credible' | 'bound' | 'unknown'
    lower: number
    upper: number
    /** Probability on the 0–1 scale, or unknown. Not a significance p-value. */
    level: number | null
  } | null
  equivalenceBounds: { lower: number; upper: number } | null
  /** A supplied excerpt, not a new verification or a source-admission receipt. */
  anchor: { excerpt: string; basis: 'claim' | 'supplied-context'; locator: string } | null
}

/** Shared defensive parsing; malformed uncertainty stays unknown, never zero. */
export function normalizeQuantitativeFinding(value: unknown): QuantitativeFinding | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const row = value as Record<string, unknown>
  const number = (raw: unknown) => (typeof raw === 'number' && Number.isFinite(raw) ? raw : null)
  const text = (raw: unknown) => (typeof raw === 'string' ? raw.slice(0, 6000).trim() : '')
  const object = (raw: unknown): Record<string, unknown> =>
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const rawInterval = object(row.interval)
  const lower = number(rawInterval.lower)
  const upper = number(rawInterval.upper)
  const level = number(rawInterval.level)
  const kind = rawInterval.kind
  const rawBounds = object(row.equivalenceBounds)
  const boundLower = number(rawBounds.lower)
  const boundUpper = number(rawBounds.upper)
  const rawAnchor = object(row.anchor)
  const excerpt = text(rawAnchor.excerpt)
  return {
    estimate: number(row.estimate),
    estimand: text(row.estimand),
    unit: text(row.unit),
    nullValue: number(row.nullValue),
    interval:
      lower !== null && upper !== null && lower <= upper
        ? {
            kind:
              kind === 'confidence' || kind === 'credible' || kind === 'bound' ? kind : 'unknown',
            lower,
            upper,
            level: level !== null && level > 0 && level < 1 ? level : null,
          }
        : null,
    equivalenceBounds:
      boundLower !== null && boundUpper !== null && boundLower < boundUpper
        ? { lower: boundLower, upper: boundUpper }
        : null,
    anchor:
      excerpt && (rawAnchor.basis === 'claim' || rawAnchor.basis === 'supplied-context')
        ? { excerpt, basis: rawAnchor.basis, locator: text(rawAnchor.locator) }
        : null,
  }
}

/** Structured scope extracted from one claim. Empty strings mean unspecified. */
export interface ClaimScope {
  population: string
  intervention: string
  exposure: string
  comparator: string
  outcome: string
  measurement: string
  timeHorizon: string
  studyDesign: string
  definition: string
  causalMode: CausalMode
  direction: ClaimDirection
  /** Omitted in legacy inputs; omission has the same authority as unknown. */
  nullMeaning?: NullMeaning
  quantitative?: QuantitativeFinding
  /** Optional input anchors for model-proposed fields; never a human review. */
  fieldOrigins?: ScopeFieldOrigin[]
  /** Boundary assumptions the sentence needs but does not state in a dimension. */
  assumptions: string[]
}

export interface ScopedClaim {
  nodeId: string
  claim: string
  scope: ClaimScope
}

export type ScopeDifferenceKind = 'same' | 'moderator' | 'different-question' | 'unknown'

export interface ScopeDifference {
  dimension: ScopeDimension
  left: string
  right: string
  kind: ScopeDifferenceKind
  rule: string
}

export type ContradictionVerdict = 'genuine' | 'scope-mismatch' | 'compatible' | 'underdetermined'
export const CONTRADICTION_VERDICTS: readonly ContradictionVerdict[] = [
  'genuine',
  'scope-mismatch',
  'compatible',
  'underdetermined',
]

export interface ModeratorEdge {
  fromId: string
  toId: string
  dimension: ScopeDimension
  left: string
  right: string
  explanation: string
}

export interface TestCost {
  /** Hands-on work. Zero is allowed for a source check. */
  effortHours: number | null
  /** Elapsed time before an answer can exist. */
  elapsedDays: number | null
  /** Money the user expects to spend, not a provider charge. */
  moneyUsd: number | null
  source: 'user' | 'estimated' | 'unknown'
}

export type UserTestCost = Pick<TestCost, 'effortHours' | 'elapsedDays' | 'moneyUsd'>

export type FutureBearing = 'positive' | 'negative' | 'inconclusive'

export interface TestPrediction {
  bearing: FutureBearing
  observation: string
  implication: string
  /** Work worth keeping even when this result is the one that lands. */
  usefulNext: string
  /**
   * Exact prepared-future ids still compatible with this observation.
   *
   * Contradiction-only tests predate prepared futures, so absence means legacy
   * rather than an empty set. Falsification portfolios require and validate it.
   */
  compatibleFutureIds?: string[]
}

export interface DiscriminatingTest {
  id: string
  kind: 'experiment' | 'source-check'
  title: string
  protocol: string[]
  discriminates: string[]
  cost: TestCost
  /** @deprecated Display-only legacy proposal. Never used by an actionable rule. */
  separation: number
  /** @deprecated Display-only legacy proposal. Never used by an actionable rule. */
  decisionImpact: number
  /** @deprecated Display-only legacy proposal. Never used by an actionable rule. */
  reusableValue: number
  predictions: TestPrediction[]
}

export interface ContradictionAnalysis {
  id: string
  left: ScopedClaim
  right: ScopedClaim
  verdict: ContradictionVerdict
  differences: ScopeDifference[]
  moderators: ModeratorEdge[]
  /** Named deterministic rules that reached `verdict`. */
  rules: string[]
  method?: string
  /** The recorded result is retained for inspection, never used as today's verdict. */
  originalInterpretation?: { method: string; verdict: ContradictionVerdict; rules: string[] }
  tests: DiscriminatingTest[]
  recommendedTestId?: string
  createdAt: number
  opId?: string
  model?: { provider: string; id: string; name: string }
}

export interface TestWeights {
  effort: number
  elapsed: number
  money: number
  /** @deprecated Kept for stored callers; deterministic ranking ignores it. */
  separation: number
  /** @deprecated Kept for stored callers; deterministic ranking ignores it. */
  decisionImpact: number
  /** @deprecated Kept for stored callers; deterministic ranking ignores it. */
  reusableValue: number
}

export const DEFAULT_TEST_WEIGHTS: TestWeights = {
  effort: 1,
  elapsed: 1,
  money: 1,
  separation: 2,
  decisionImpact: 2,
  reusableValue: 1,
}

const DIMENSIONS: readonly ScopeDimension[] = [
  'population',
  'intervention',
  'exposure',
  'comparator',
  'outcome',
  'measurement',
  'timeHorizon',
  'studyDesign',
  'definition',
  'causalMode',
  'assumptions',
]

const QUESTION_DIMENSIONS = new Set<ScopeDimension>(['outcome', 'measurement', 'definition'])

const fold = (value: string): string =>
  value
    .normalize('NFKC')
    .toLocaleLowerCase('en-US')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

const valueOf = (scope: ClaimScope, dimension: ScopeDimension): string => {
  if (dimension === 'causalMode') return scope.causalMode
  if (dimension === 'assumptions') {
    return [...new Set(scope.assumptions.map(fold).filter(Boolean))].sort().join(' | ')
  }
  return scope[dimension]
}

const known = (value: string): boolean => value !== '' && value !== 'unspecified'

export function emptyClaimScope(): ClaimScope {
  return {
    population: '',
    intervention: '',
    exposure: '',
    comparator: '',
    outcome: '',
    measurement: '',
    timeHorizon: '',
    studyDesign: '',
    definition: '',
    causalMode: 'unspecified',
    direction: 'unspecified',
    assumptions: [],
  }
}

/**
 * Compare every scope dimension without deciding the final label.
 *
 * Lexical equality is deliberately conservative. A model may normalize
 * "adolescents" and "ages 12–17" to the same value during extraction; this
 * function will not invent that equivalence after the fact.
 */
export function scopeDifferences(left: ClaimScope, right: ClaimScope): ScopeDifference[] {
  return DIMENSIONS.map((dimension) => {
    const a = valueOf(left, dimension)
    const b = valueOf(right, dimension)
    if (!known(a) || !known(b)) {
      return {
        dimension,
        left: a,
        right: b,
        kind: 'unknown',
        rule: 'scope.dimension.unspecified',
      }
    }
    if (fold(a) === fold(b)) {
      return {
        dimension,
        left: a,
        right: b,
        kind: 'same',
        rule: 'scope.dimension.equal',
      }
    }
    return {
      dimension,
      left: a,
      right: b,
      kind: QUESTION_DIMENSIONS.has(dimension) ? 'different-question' : 'moderator',
      rule: QUESTION_DIMENSIONS.has(dimension)
        ? 'scope.dimension.changes-question'
        : 'scope.dimension.moderates-effect',
    }
  })
}

const opposite = (a: ClaimDirection, b: ClaimDirection): boolean =>
  (a === 'positive' && b === 'negative') || (b === 'positive' && a === 'negative')

function quantitativeConflict(left: ClaimScope, right: ClaimScope): string | null {
  const a = normalizeQuantitativeFinding(left.quantitative)
  const b = normalizeQuantitativeFinding(right.quantitative)
  if (!a?.interval || !b?.interval || !a.anchor || !b.anchor) return null
  if (
    !a.estimand ||
    !b.estimand ||
    fold(a.estimand) !== fold(b.estimand) ||
    !a.unit ||
    !b.unit ||
    fold(a.unit) !== fold(b.unit) ||
    a.nullValue === null ||
    a.nullValue !== b.nullValue ||
    a.interval.kind === 'unknown' ||
    a.interval.kind !== b.interval.kind ||
    (a.interval.kind !== 'bound' &&
      (a.interval.level === null || a.interval.level !== b.interval.level))
  )
    return null
  if (a.estimate !== null && b.estimate !== null && a.estimate === b.estimate) return null
  for (const [scope, finding] of [
    [left, a],
    [right, b],
  ] as const) {
    if (
      finding.estimate !== null &&
      (finding.estimate < finding.interval!.lower || finding.estimate > finding.interval!.upper)
    )
      return null
    if (scope.direction !== 'null') continue
    // A failure to reject and an estimated zero cannot establish absence. An
    // equivalence claim needs its declared margin and uncertainty inside it.
    if (scope.nullMeaning !== 'equivalent' || !finding.equivalenceBounds) return null
    if (
      finding.interval!.lower < finding.equivalenceBounds.lower ||
      finding.interval!.upper > finding.equivalenceBounds.upper ||
      finding.equivalenceBounds.lower >= finding.nullValue! ||
      finding.equivalenceBounds.upper <= finding.nullValue!
    )
      return null
    const other = finding === a ? b : a
    if (!(
      other.interval!.upper < finding.equivalenceBounds.lower ||
      other.interval!.lower > finding.equivalenceBounds.upper
    ))
      return null
  }
  return a.interval.upper < b.interval.lower || b.interval.upper < a.interval.lower
    ? 'contradiction.same-scope-disjoint-declared-uncertainty'
    : null
}

/** A deterministic verdict over already-structured claims. */
export function classifyContradiction(
  left: ScopedClaim,
  right: ScopedClaim,
): Pick<ContradictionAnalysis, 'verdict' | 'differences' | 'moderators' | 'rules'> {
  const differences = scopeDifferences(left.scope, right.scope)
  const differentQuestion = differences.filter((row) => row.kind === 'different-question')
  const moderators = differences.filter((row) => row.kind === 'moderator')
  // `genuine` is the loud label. It requires every structured boundary to be
  // known and equal; an extractor's empty field is uncertainty, not sameness.
  // Intervention and exposure are alternate vocabularies, so one equal known
  // channel may carry the pair only when the other is unspecified on both sides.
  const intervention = differences.find((row) => row.dimension === 'intervention')!
  const exposure = differences.find((row) => row.dimension === 'exposure')!
  const unknowns = differences.filter(
    (row) =>
      row.kind === 'unknown' &&
      !(
        (row.dimension === 'intervention' &&
          !known(row.left) &&
          !known(row.right) &&
          exposure.kind === 'same') ||
        (row.dimension === 'exposure' &&
          !known(row.left) &&
          !known(row.right) &&
          intervention.kind === 'same')
      ),
  )
  const comparableCore = differentQuestion.length === 0 && unknowns.length === 0
  const directionsKnown =
    left.scope.direction !== 'unspecified' && right.scope.direction !== 'unspecified'

  let verdict: ContradictionVerdict
  const rules: string[] = []
  if (differentQuestion.length) {
    verdict = 'scope-mismatch'
    rules.push('contradiction.different-outcome-or-definition')
  } else if (!directionsKnown || !comparableCore) {
    verdict = 'underdetermined'
    rules.push('contradiction.missing-comparable-scope-or-boundary')
  } else if (left.scope.direction === 'mixed' || right.scope.direction === 'mixed') {
    verdict = 'underdetermined'
    rules.push('contradiction.mixed-direction-needs-interpretation')
  } else if (
    !opposite(left.scope.direction, right.scope.direction) &&
    left.scope.direction !== 'null' &&
    right.scope.direction !== 'null' &&
    !left.scope.quantitative &&
    !right.scope.quantitative
  ) {
    verdict = 'compatible'
    rules.push('contradiction.directions-compatible')
  } else if (moderators.length) {
    verdict = 'scope-mismatch'
    rules.push('contradiction.effect-moderated-by-scope')
  } else if (
    left.scope.quantitative ||
    right.scope.quantitative ||
    left.scope.direction === 'null' ||
    right.scope.direction === 'null'
  ) {
    const conflict = quantitativeConflict(left.scope, right.scope)
    verdict = conflict ? 'genuine' : 'underdetermined'
    rules.push(conflict ?? 'contradiction.uncertainty-does-not-establish-incompatibility')
  } else {
    verdict = 'genuine'
    rules.push('contradiction.same-scope-opposite-direction')
  }

  return {
    verdict,
    differences,
    rules,
    moderators: moderators.map((row) => ({
      fromId: left.nodeId,
      toId: right.nodeId,
      dimension: row.dimension,
      left: row.left,
      right: row.right,
      explanation: `${row.dimension} differs, so the two effects need not describe the same case.`,
    })),
  }
}

const finiteFloor = (value: number | null): number | null =>
  value === null || !Number.isFinite(value) ? null : Math.max(0, value)

export function normalizeTest(test: DiscriminatingTest): DiscriminatingTest {
  const byBearing = new Map<FutureBearing, TestPrediction>()
  for (const prediction of test.predictions) {
    if (!byBearing.has(prediction.bearing)) byBearing.set(prediction.bearing, prediction)
  }
  return {
    ...test,
    id: test.id.trim(),
    title: test.title.trim(),
    protocol: test.protocol.map((row) => row.trim()).filter(Boolean),
    discriminates: [...new Set(test.discriminates.map((row) => row.trim()).filter(Boolean))],
    separation: Math.max(0, Math.min(1, test.separation)),
    decisionImpact: Math.max(0, Math.min(1, test.decisionImpact)),
    reusableValue: Math.max(0, Math.min(1, test.reusableValue)),
    cost: {
      effortHours: finiteFloor(test.cost.effortHours),
      elapsedDays: finiteFloor(test.cost.elapsedDays),
      moneyUsd: finiteFloor(test.cost.moneyUsd),
      source: test.cost.source,
    },
    predictions: (['positive', 'negative', 'inconclusive'] as const).flatMap((bearing) => {
      const row = byBearing.get(bearing)
      return row
        ? [
            {
              ...row,
              observation: row.observation.trim(),
              implication: row.implication.trim(),
              usefulNext: row.usefulNext.trim(),
              ...(row.compatibleFutureIds
                ? {
                    compatibleFutureIds: [
                      ...new Set(row.compatibleFutureIds.map((id) => id.trim()).filter(Boolean)),
                    ].sort(),
                  }
                : {}),
            },
          ]
        : []
    }),
  }
}

/** Replace proposed costs with the user's figures and make their authorship explicit. */
export function withUserTestCost(test: DiscriminatingTest, cost: UserTestCost): DiscriminatingTest {
  return normalizeTest({ ...test, cost: { ...cost, source: 'user' } })
}

/** Historical API name: this checks proposal structure only. Filled fields do
 * not establish feasibility, statistical discrimination or reproducibility. */
export function executableDiscriminatingTest(test: DiscriminatingTest): boolean {
  const normalized = normalizeTest(test)
  return (
    normalized.id.length > 0 &&
    normalized.title.length > 0 &&
    normalized.protocol.length > 0 &&
    normalized.discriminates.length > 0 &&
    normalized.predictions.length === 3 &&
    normalized.predictions.every(
      (row) =>
        row.observation.length > 0 && row.implication.length > 0 && row.usefulNext.length > 0,
    )
  )
}

/** Missing costs are penalized rather than guessed to be free. */
const costValue = (value: number | null, unknown: number): number =>
  value === null ? unknown : Math.log1p(value)

/**
 * A deterministic affordability index.
 *
 * The name is retained for API compatibility. It intentionally reads only the
 * cost record. Model-authored separation, decision-impact, and reusable-value
 * floats are legacy display data and cannot move a test or earn a recommendation.
 */
export function testScore(
  raw: DiscriminatingTest,
  weights: TestWeights = DEFAULT_TEST_WEIGHTS,
): number {
  const test = normalizeTest(raw)
  const cost =
    weights.effort * costValue(test.cost.effortHours, 2) +
    weights.elapsed * costValue(test.cost.elapsedDays, 2) +
    weights.money * costValue(test.cost.moneyUsd, 4)
  return 1 / Math.max(1, cost)
}

const compareStableId = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0

/** Cheapest mechanically comparable proposal first; stable id breaks exact ties. */
export function rankDiscriminatingTests(
  tests: readonly DiscriminatingTest[],
  weights: TestWeights = DEFAULT_TEST_WEIGHTS,
): DiscriminatingTest[] {
  return tests
    .map(normalizeTest)
    .filter(executableDiscriminatingTest)
    .sort((a, b) => testScore(b, weights) - testScore(a, weights) || compareStableId(a.id, b.id))
}

export function completeContradictionAnalysis(
  base: Omit<ContradictionAnalysis, 'verdict' | 'differences' | 'moderators' | 'rules'>,
  weights: TestWeights = DEFAULT_TEST_WEIGHTS,
): ContradictionAnalysis {
  const classified = classifyContradiction(base.left, base.right)
  const tests = rankDiscriminatingTests(base.tests, weights)
  return {
    ...base,
    ...classified,
    method: CONTRADICTION_METHOD,
    tests,
    ...(tests[0] ? { recommendedTestId: tests[0].id } : {}),
  }
}

/** Re-rank a stored analysis after the user supplies the cost of one test. */
export function updateContradictionTestCost(
  analysis: ContradictionAnalysis,
  testId: string,
  cost: UserTestCost,
  weights: TestWeights = DEFAULT_TEST_WEIGHTS,
): ContradictionAnalysis {
  if (!analysis.tests.some((test) => test.id === testId)) {
    throw new Error(`Unknown discriminating test: ${testId}`)
  }
  return completeContradictionAnalysis(
    {
      id: analysis.id,
      left: analysis.left,
      right: analysis.right,
      tests: analysis.tests.map((test) =>
        test.id === testId ? withUserTestCost(test, cost) : test,
      ),
      createdAt: analysis.createdAt,
      ...(analysis.originalInterpretation
        ? { originalInterpretation: analysis.originalInterpretation }
        : {}),
      ...(analysis.opId ? { opId: analysis.opId } : {}),
      ...(analysis.model ? { model: analysis.model } : {}),
    },
    weights,
  )
}
